import { useEffect } from 'react';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

// Cache for storing loaded models
const modelCache = new Map();

/**
 * Component that preloads 3D models in the background
 * to improve perceived performance
 */
const ModelPreloader = ({ modelPaths = [] }) => {
  useEffect(() => {
    // Skip if no models to preload
    if (!modelPaths.length) return;
    
    // Setup Draco loader
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    dracoLoader.setDecoderConfig({ type: 'js' });
    
    // Setup GLTF loader
    const gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
    
    // Create a low priority loader for each model
    modelPaths.forEach(modelPath => {
      // Skip if already cached
      if (modelCache.has(modelPath)) return;
      
      console.log(`Preloading model: ${modelPath}`);
      
      // Mark as "loading" in cache to prevent duplicate loads
      modelCache.set(modelPath, 'loading');
      
      // Load with low priority to avoid blocking main UI
      setTimeout(() => {
        gltfLoader.load(
          modelPath,
          (gltf) => {
            console.log(`Preloaded model: ${modelPath}`);
            modelCache.set(modelPath, gltf);
          },
          (xhr) => {
            const progress = Math.round(xhr.loaded / xhr.total * 100);
            console.log(`Preload progress for ${modelPath}: ${progress}%`);
          },
          (error) => {
            console.error(`Failed to preload ${modelPath}:`, error);
            modelCache.delete(modelPath); // Remove from cache to allow retry
          }
        );
      }, 2000); // Delay preloading by 2 seconds after component mounts
    });
    
    // Cleanup
    return () => {
      dracoLoader.dispose();
    };
  }, [modelPaths]);
  
  // This component doesn't render anything visible
  return null;
};

// Utility functions for the cache
export const getPreloadedModel = (path) => modelCache.get(path);
export const isModelPreloaded = (path) => modelCache.has(path) && modelCache.get(path) !== 'loading';

export default ModelPreloader; 